import { Fragment, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCourse, useExtractModule, useIndexCourse, useSyncCourse } from "../api/queries";
import type { MoodleModule, MoodleSection } from "../api/types";
import { JobNotice } from "../components/JobNotice";
import { ErrorBanner, Loading, RelativeTime } from "../components/ui";
import {
  BrandButton,
  BrandLinkButton,
  CoursePageHeader,
  DataTable,
  EmptyState,
  HeadCell,
  NumberPager,
  RowCell,
  StatTile,
} from "../components/courses/ui";
import { pad, rowClass } from "../components/courses/styles";
import { bytes } from "../lib/format";

interface SectionGroup {
  key: string;
  number: number | null;
  section?: MoodleSection;
  name: string | null;
  modules: MoodleModule[];
}

// Modules carry section_number; course.sections has each section's summary
// and visibility. Modules without a section go last.
function groupBySection(sections: MoodleSection[], modules: MoodleModule[]): SectionGroup[] {
  const groups = new Map<number | null, SectionGroup>();
  for (const s of sections.filter((s) => !s.deleted_at)) {
    groups.set(s.section_number, { key: `s${s.moodle_id}`, number: s.section_number, section: s, name: s.name, modules: [] });
  }
  for (const m of modules) {
    const n = m.section_number;
    if (!groups.has(n)) groups.set(n, { key: n === null ? "none" : `n${n}`, number: n, name: m.section_name, modules: [] });
    groups.get(n)!.modules.push(m);
  }
  return [...groups.values()].sort((a, b) => (a.number ?? Infinity) - (b.number ?? Infinity));
}

const MODULES_PER_PAGE = 20;

export function CourseDetailPage() {
  const moodleId = Number(useParams().moodleId);
  const course = useCourse(moodleId);
  const syncCourse = useSyncCourse();
  const indexCourse = useIndexCourse();
  const extractModule = useExtractModule();
  const [showRemoved, setShowRemoved] = useState(false);
  const [page, setPage] = useState(0);

  const groups = useMemo(() => {
    if (!course.data) return [];
    const modules = course.data.modules.filter((m) => showRemoved || !m.deleted_at);
    return groupBySection(course.data.sections, modules);
  }, [course.data, showRemoved]);

  // Paginate by module; each page repeats the header of the section its
  // first module belongs to, so a page never starts mid-section unlabelled.
  const pages = useMemo(() => {
    type Item = { g: SectionGroup; m: MoodleModule | null };
    const flat = groups.flatMap((g): Item[] => (g.modules.length ? g.modules.map((m) => ({ g, m })) : [{ g, m: null }]));
    const out: Item[][] = [];
    for (let i = 0; i < flat.length; i += MODULES_PER_PAGE) out.push(flat.slice(i, i + MODULES_PER_PAGE));
    return out;
  }, [groups]);
  const current = pages[Math.min(page, Math.max(0, pages.length - 1))] ?? [];

  if (course.isLoading) return <div className="px-8 py-10"><Loading /></div>;
  if (course.error || !course.data) {
    return (
      <div className="px-8 py-10">
        <ErrorBanner error={course.error ?? "Course not found"} />
        <Link to="/courses" className="text-sm font-medium text-[#0B5785] hover:underline">← Back to courses</Link>
      </div>
    );
  }

  const c = course.data;
  const s = c.stats;
  const liveModules = c.modules.filter((m) => !m.deleted_at);
  const removedCount = c.modules.length - liveModules.length;
  const notExtracted = liveModules.filter((m) => m.needs_extraction && !m.extraction_error).length;
  const summary = c.summary ? stripTags(c.summary).trim() : "";

  return (
    <div className="px-8 py-10">
      <CoursePageHeader
        back="/courses"
        title={c.fullname}
        subtitle={<>{c.shortname} · Moodle id {c.moodle_id} · synced <RelativeTime iso={c.synced_at} />{!c.visible && " · hidden in Moodle"}</>}
        actions={
          <>
            <BrandButton className="min-w-[200px]" loading={syncCourse.isPending} onClick={() => syncCourse.mutate(moodleId)}>
              Re - Sync From Moodle
            </BrandButton>
            <BrandLinkButton to={`/approvals?course=${moodleId}`} className="min-w-[200px]">Review Content</BrandLinkButton>
            <BrandButton
              variant="primary"
              className="min-w-[200px] min-h-[62px]"
              loading={indexCourse.isPending}
              onClick={() => indexCourse.mutate(moodleId)}
              title="Sync, extract and index this one course. Only approved documents are embedded."
            >
              Sync + Extract + Index
            </BrandButton>
          </>
        }
      />
      <div className="mt-4 border-t border-gray-200" />

      <div className="mt-6">
        <ErrorBanner error={syncCourse.error || indexCourse.error || extractModule.error} />
        <JobNotice kind="sync" />
        <JobNotice kind="extract" />
        <JobNotice kind="index" />
      </div>

      <div className="grid grid-cols-2 gap-3 rounded-xl border border-gray-100 bg-white p-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
        <StatTile label="Modules" value={pad(s?.modules ?? liveModules.length)} hint={notExtracted ? `${notExtracted} not extracted yet` : undefined} />
        <StatTile label="Extraction failed" value={s ? pad(s.extraction_failed) : "—"} tone={s?.extraction_failed ? "danger" : undefined} />
        <StatTile label="Documents" value={s ? pad(s.documents) : "—"} />
        <StatTile
          label="Pending review"
          tone={s?.pending ? "warning" : undefined}
          value={s ? <Link to={`/approvals?course=${moodleId}`} className="hover:underline">{pad(s.pending)}</Link> : "—"}
        />
        <StatTile label="Approved" value={s ? pad(s.approved) : "—"} hint={s ? `${s.rejected} rejected · ${s.retired} retired` : undefined} />
        <StatTile
          label="Indexed"
          value={s ? pad(s.indexed) : "—"}
          hint={s && s.approved > s.indexed ? `${s.approved - s.indexed} approved, not yet indexed` : "Retrievable by the tutor"}
        />
        <StatTile label="Sections" value={pad(c.sections.filter((x) => !x.deleted_at).length)} />
      </div>

      {summary && (
        <section className="mt-12 px-9">
          <h2 className="border-b border-gray-200 px-2.5 pb-4 text-[15px] font-medium text-gray-900">Summary</h2>
          {/* Moodle summaries are HTML; show as text rather than injecting markup. */}
          <p className="mt-4 whitespace-pre-line text-xs leading-relaxed text-gray-500">{summary}</p>
        </section>
      )}

      <section className="mt-10 px-5">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 px-1">
          <h2 className="text-[15px] font-medium text-gray-900">Content by section</h2>
          {removedCount > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                className="accent-[#0B5785]"
                checked={showRemoved}
                onChange={(e) => {
                  setShowRemoved(e.target.checked);
                  setPage(0);
                }}
              />
              Show {removedCount} removed
            </label>
          )}
        </div>
        {!groups.length ? <EmptyState>No sections or modules.</EmptyState> : (
          <DataTable
            head={
              <>
                <HeadCell className="w-28">cmid</HeadCell>
                <HeadCell>Name</HeadCell>
                <HeadCell>Type</HeadCell>
                <HeadCell>Files</HeadCell>
                <HeadCell>Extraction</HeadCell>
                <HeadCell className="text-center">Action</HeadCell>
              </>
            }
          >
            {current.map(({ g, m }, i) => (
              <Fragment key={m ? m.moodle_cmid : g.key}>
                {(i === 0 || current[i - 1].g !== g) && <SectionRow group={g} />}
                {m ? (
                  <ModuleRow module={m} courseMoodleId={moodleId} extract={extractModule} />
                ) : (
                  <tr className={rowClass}><RowCell colSpan={6} className="text-center text-xs text-gray-400">No modules in this section.</RowCell></tr>
                )}
              </Fragment>
            ))}
          </DataTable>
        )}
        <NumberPager page={Math.min(page, Math.max(0, pages.length - 1))} pages={pages.length} hasNext={page < pages.length - 1} onChange={setPage} />
      </section>
    </div>
  );
}

function SectionRow({ group: g }: { group: SectionGroup }) {
  return (
    <tr>
      <td colSpan={6} className="border-b border-gray-200 bg-[#0B5785]/[0.04] px-7 py-2">
        <div className="flex flex-wrap items-baseline gap-2 text-sm font-semibold text-[#0B5785]">
          {g.number !== null && <span className="text-[#0B5785]/60">{g.number}.</span>}
          {g.name || <span className="font-normal text-gray-400">{g.number === null ? "No section" : "Untitled section"}</span>}
          {g.section && !g.section.visible && <span className="text-xs font-normal text-gray-500">hidden</span>}
          <span className="ml-auto text-xs font-normal text-gray-500">{g.modules.length} module{g.modules.length === 1 ? "" : "s"}</span>
        </div>
        {g.section?.summary && <p className="mt-0.5 line-clamp-2 text-xs font-normal text-gray-500">{stripTags(g.section.summary)}</p>}
      </td>
    </tr>
  );
}

function ModuleRow({ module: m, courseMoodleId, extract }: {
  module: MoodleModule;
  courseMoodleId: number;
  extract: ReturnType<typeof useExtractModule>;
}) {
  const files = m.files.filter((f) => !f.deleted_at);
  return (
    <tr className={`${rowClass} ${m.deleted_at ? "opacity-50" : ""}`}>
      <RowCell className="tabular-nums">{m.moodle_cmid}</RowCell>
      <RowCell className="max-w-[260px]">
        {m.url ? <a href={m.url} target="_blank" rel="noreferrer" className="hover:text-[#0B5785] hover:underline">{m.name}</a> : m.name}
        {m.deleted_at && <span className="ml-2 text-xs text-red-600">removed</span>}
        {!m.visible && <span className="ml-2 text-xs text-gray-500">hidden</span>}
      </RowCell>
      <RowCell>{m.modname}</RowCell>
      <RowCell>
        {files.length ? files.map((f) => (
          <div key={f.filename}>{f.filename} <span className="text-gray-500">({bytes(f.filesize)})</span></div>
        )) : <span className="text-gray-400">----</span>}
      </RowCell>
      <RowCell className="max-w-xs">
        {m.deleted_at ? null : m.extraction_error ? (
          <>
            <span className="font-medium text-red-600">failed</span>
            <div className="mt-0.5 break-words text-xs text-red-700">{m.extraction_error}</div>
          </>
        ) : m.needs_extraction ? (
          <span className="text-amber-700">{m.extracted_hash ? "changed, re-extract" : "not extracted"}</span>
        ) : (
          "up to date"
        )}
      </RowCell>
      <RowCell className="whitespace-nowrap text-center">
        {!m.deleted_at && (
          <span className="inline-flex items-center gap-2">
            <Link
              to={`/approvals?course=${courseMoodleId}&module=${m.moodle_cmid}`}
              className="rounded-md px-2.5 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Review
            </Link>
            <BrandButton
              size="sm"
              loading={extract.isPending && extract.variables === m.moodle_cmid}
              disabled={extract.isPending}
              onClick={() => extract.mutate(m.moodle_cmid)}
            >
              {m.extraction_error ? "Retry" : "Re - Extract"}
            </BrandButton>
          </span>
        )}
      </RowCell>
    </tr>
  );
}

function stripTags(html: string) {
  return new DOMParser().parseFromString(html, "text/html").body.textContent ?? "";
}
